import { UrlRewriter } from '@fullstory/url-rewriter';
import {
  CSSSelectorsWithColonsTransform,
  CSSBoxSizingTransform,
  CSSCursorTransform,
  CSSDisplayInlineTransform,
  CSSFillUrlTransform,
  CSSMaskFontTransform,
  CSSSafariFontFaceSrcTransformer,
  CSSScrollPropertiesTransform,
  CSSTreeMediaFeaturesTransform,
  CSSTreeSelectorsTransform,
  CSSTreeTransformer,
  CSSTreeUrlTransform,
  CSSWildcardDisplayTransform,
  MozToWebkitTransform,
} from './csstreetransforms';
import { CSSTreeMediaTransform } from './mediaquerytransforms';

type RewriteResult = { value: string, success: boolean }

// An object that can rewrite CSS stylesheets and inline styles.
export interface CSSRewriter {
  // Rewrite a media query.
  rewriteMedia(media: MediaList): string;

  // Rewrite the style attribute of an element. The element is not modified.
  rewriteStyleAttribute(elem: HTMLElement | SVGElement): string;

  // Rewrite a style attribute value.
  rewriteStyleAttributeValue(value: string): string;

  // Rewrite a stylesheet object from a <link> or <style> element. The
  // stylesheet is not modified.
  rewriteStyleSheet(sheet: CSSStyleSheet): string;

  // Rewrite the text of a stylesheet.
  rewriteStyleSheetText(cssText: string): RewriteResult;

  // Rewrite a single CSS rule in isolation, such as you would pass to
  // insertRule().
  rewriteStyleRuleText(ruleText: string): string;
}

/**
 * A factory function that returns an instance of CSSRewriter configured based
 * on the enabled feature set.
 */
export function createCSSRewriter(
  urlRewriter: UrlRewriter,
): CSSRewriter {
  return new CSSTreeRewriter(urlRewriter);
}

// A CSSRewriter that rewrites CSS in pure JavaScript using the css-tree
// library. This rewriter can be used in both window and worker
// contexts, and its behavior is consistent on every browser.
export class CSSTreeRewriter implements CSSRewriter {
  private _styleAttributeValueCache = new Map<string, string>();
  private _styleSheetTextCache = new Map<string, RewriteResult>();

  constructor(private _urlRewriter: UrlRewriter) {
  }

  // TODO(sethf): This is never called anywhere, but we *should* be calling it
  // for <link media> attributes. :( I'll take care of that in a future PR.
  rewriteMedia(media: MediaList): string {
    if (media.length === 0) {
      return '';
    }
    const mediaList: string[] = [];
    for (const m of media) {
      mediaList.push(m);
    }
    const mediaListAsString = mediaList.join(', ');

    const transformer = new CSSTreeTransformer(mediaListAsString, 'mediaQueryList');
    transformer.run(CSSTreeMediaFeaturesTransform());
    return transformer.serialize();
  }

  rewriteStyleAttribute(elem: HTMLElement | SVGElement): string {
    const value = elem.getAttribute('style');
    if (!value) {
      return '';
    }
    return this.rewriteStyleAttributeValue(value);
  }

  rewriteStyleAttributeValue(value: string): string {
    const cachedRewrittenValue = this._styleAttributeValueCache.get(value);
    if (cachedRewrittenValue !== undefined) {
      return cachedRewrittenValue;
    }

    // Note that we omit the transforms that never affect declarations.
    const transformer = new CSSTreeTransformer(value, 'declarationList');
    transformer.runFused([
      CSSTreeUrlTransform(this._urlRewriter),
      CSSMaskFontTransform(),
      CSSBoxSizingTransform(),
      CSSScrollPropertiesTransform(),
      CSSDisplayInlineTransform(),
      CSSFillUrlTransform(this._urlRewriter.baseUrl),
      CSSSafariFontFaceSrcTransformer(),
      CSSCursorTransform(),
      MozToWebkitTransform(),
    ]);

    const rewrittenValue = transformer.serialize();
    this._styleAttributeValueCache.set(value, rewrittenValue);
    return rewrittenValue;
  }

  rewriteStyleSheet(sheet: CSSStyleSheet): string {
    const ownerNode = sheet.ownerNode;
    if (!ownerNode) {
      throw new Error(`Can't parse stylesheet with no owner`);
    }
    const textContent = ownerNode.textContent;
    if (!textContent) {
      throw new Error(`Can't obtain stylesheet text`);
    }
    return this.rewriteStyleSheetText(textContent).value;
  }

  rewriteStyleSheetText(cssText: string, context: 'stylesheet' | 'rule' = 'stylesheet'): RewriteResult {
    const cachedRewriteResult = this._styleSheetTextCache.get(cssText);
    if (cachedRewriteResult !== undefined) {
      return cachedRewriteResult;
    }

    const transformer = new CSSTreeTransformer(cssText, context);

    transformer.runFused([
      CSSTreeUrlTransform(this._urlRewriter),
      CSSTreeMediaFeaturesTransform(),
      CSSTreeMediaTransform(),
      CSSMaskFontTransform(),
      CSSBoxSizingTransform(),
      CSSScrollPropertiesTransform(),
      CSSDisplayInlineTransform(),
      CSSTreeSelectorsTransform(),
      CSSFillUrlTransform(this._urlRewriter.baseUrl),
      CSSSafariFontFaceSrcTransformer(),
      CSSWildcardDisplayTransform(),
      CSSCursorTransform(),
      MozToWebkitTransform(),
      CSSSelectorsWithColonsTransform(),
    ]);

    const result = {
      value: transformer.serialize(),
      success: !transformer.hadParseFailure(),
    };
    this._styleSheetTextCache.set(cssText, result);
    return result;
  }

  rewriteStyleRuleText(ruleText: string): string {
    // FIXME: the context is too general here, consider changing it to 'rule' and handling
    // specific cases for @media etc
    return this.rewriteStyleSheetText(ruleText, 'stylesheet').value;
  }
}
